import AddIcon from "@mui/icons-material/Add";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import EditIcon from "@mui/icons-material/Edit";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import {
  GridColDef,
  GridActionsCellItem,
  GridRenderCellParams,
} from "@mui/x-data-grid-premium";
import { HotkeyCompliantDataGrid } from "components/common/dataGrid/HotkeyCompliantDataGrid";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import { useCallback, useMemo, useState } from "react";
import {
  LabelCoveragePriority,
  LabelReservationSettingsCreate,
  LabelReservationSettingsRead,
  NoReservationBehaviour,
  UserReadAll,
  UserSettingsAssignmentRead,
  useAssignUserLabelReservationSettingsMutation,
  useCreateLabelReservationSettingsMutation,
  useDeleteLabelReservationSettingsMutation,
  useGetUsersQuery,
  useListLabelReservationSettingsAssignmentsQuery,
  useListLabelReservationSettingsQuery,
  useSetDefaultLabelReservationSettingsMutation,
  useUnassignUserLabelReservationSettingsMutation,
  useUpdateLabelReservationSettingsMutation,
} from "src/store/api/generatedApi";
import { decomposePriority } from "./labelCoveragePriorityUtils";
import { SettingsProfileForm } from "./SettingsProfileForm";

dayjs.extend(utc);

const AssignUserDialog = ({
  open,
  onClose,
  users,
  onAssign,
}: {
  open: boolean;
  onClose: () => void;
  users: UserReadAll[];
  onAssign: (userId: string) => void;
}) => {
  const { t } = useTenantTranslation();
  const [userId, setUserId] = useState("");

  const handleSubmit = useCallback(() => {
    if (userId) {
      onAssign(userId);
      onClose();
    }
  }, [userId, onAssign, onClose]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t("assignUser")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label={t("labeller")}
            value={userId}
            onChange={(e) => {
              setUserId(e.target.value);
            }}
            select
            size="small"
            slotProps={{
              select: {
                MenuProps: { PaperProps: { sx: { maxHeight: 300 } } },
              },
            }}
          >
            {users.map((u) => (
              <MenuItem key={u.uid} value={u.uid}>
                {u.name} ({u.email})
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("cancel")}</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!userId}>
          {t("assignUser")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const SettingsProfilesList = () => {
  const { t } = useTenantTranslation();
  const tenantName = useTenant();
  const { data: settingsData, isLoading } =
    useListLabelReservationSettingsQuery({ tenantName });
  const { data: assignmentsData } =
    useListLabelReservationSettingsAssignmentsQuery({ tenantName });
  const { data: usersData } = useGetUsersQuery({ tenantName });
  const labellers = useMemo(
    () =>
      usersData?.filter(
        (u) =>
          u.is_enabled &&
          u.permissions.some((p) => p.permission === "video_labelling")
      ) ?? [],
    [usersData]
  );

  const [createSettings] = useCreateLabelReservationSettingsMutation();
  const [updateSettings, { isLoading: isUpdatingSettings }] =
    useUpdateLabelReservationSettingsMutation();
  const [deleteSettings] = useDeleteLabelReservationSettingsMutation();
  const [setDefault] = useSetDefaultLabelReservationSettingsMutation();
  const [assignUser] = useAssignUserLabelReservationSettingsMutation();
  const [unassignUser] = useUnassignUserLabelReservationSettingsMutation();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<LabelReservationSettingsRead | null>(
    null
  );
  const [assignDialogSettingsId, setAssignDialogSettingsId] = useState<
    number | null
  >(null);

  const assignmentsBySettingsId = useMemo(() => {
    const map = new Map<number, UserSettingsAssignmentRead[]>();
    for (const a of assignmentsData?.items ?? []) {
      const existing = map.get(a.settings_id) ?? [];
      map.set(a.settings_id, [...existing, a]);
    }
    return map;
  }, [assignmentsData]);

  const defaultRow = useMemo(
    () => settingsData?.items.find((s) => s.default) ?? null,
    [settingsData]
  );

  const otherFallbackPriorities = useMemo(() => {
    const priorities = new Set<number>();
    for (const profile of settingsData?.items ?? []) {
      if (profile.id === editing?.id) continue;
      if (profile.fallback_priority != null) {
        priorities.add(profile.fallback_priority);
      }
    }
    return priorities;
  }, [settingsData, editing]);

  const sortedRows = useMemo(() => {
    if (!settingsData) return [];
    return [...settingsData.items].sort((a, b) => {
      if (a.default && !b.default) return -1;
      if (!a.default && b.default) return 1;
      return 0;
    });
  }, [settingsData]);

  const handleCreate = useCallback(
    async (formData: LabelReservationSettingsCreate) => {
      await createSettings({
        tenantName,
        labelReservationSettingsCreate: formData,
      }).unwrap();
    },
    [createSettings, tenantName]
  );

  const handleUpdate = useCallback(
    async (formData: LabelReservationSettingsCreate) => {
      if (!editing) return;
      await updateSettings({
        tenantName,
        settingsId: editing.id,
        labelReservationSettingsUpdate: formData,
      }).unwrap();
    },
    [updateSettings, tenantName, editing]
  );

  const handleDelete = useCallback(
    (id: number) => {
      void deleteSettings({ tenantName, settingsId: id });
    },
    [deleteSettings, tenantName]
  );

  const handleSetDefault = useCallback(
    (id: number) => {
      void setDefault({ tenantName, settingsId: id });
    },
    [setDefault, tenantName]
  );

  const handleAssign = useCallback(
    (settingsId: number, userId: string) => {
      void assignUser({
        tenantName,
        userSettingsAssignment: {
          user_id: userId,
          settings_id: settingsId,
        },
      });
    },
    [assignUser, tenantName]
  );

  const handleAdjustPriority = useCallback(
    (settingsId: number, delta: number) => {
      const row = settingsData?.items.find((s) => s.id === settingsId);
      if (row?.fallback_priority == null) return;
      const next = row.fallback_priority + delta;
      if (next < 0) return;
      void updateSettings({
        tenantName,
        settingsId,
        labelReservationSettingsUpdate: { fallback_priority: next },
      });
    },
    [settingsData, updateSettings, tenantName]
  );

  const handleUnassign = useCallback(
    (userId: string) => {
      void unassignUser({ tenantName, userId });
    },
    [unassignUser, tenantName]
  );

  const columns = useMemo(
    (): GridColDef<LabelReservationSettingsRead>[] => [
      {
        field: "name",
        headerName: t("settingsName"),
        width: 180,
        renderCell: ({ row, value }) => (
          <span style={{ fontWeight: row.default ? 700 : 400 }}>{value}</span>
        ),
      },
      {
        field: "label_coverage_priority",
        headerName: t("labelRecentFirst"),
        width: 120,
        renderCell: (
          params: GridRenderCellParams<LabelReservationSettingsRead>
        ) => (
          <Checkbox
            checked={
              decomposePriority(params.value as LabelCoveragePriority)
                .labelRecent
            }
            disabled
            size="small"
          />
        ),
      },
      {
        field: "base_strategy",
        headerName: t("coveragePriority"),
        width: 140,
        valueGetter: (_value, row) =>
          decomposePriority(row.label_coverage_priority).baseStrategy,
        valueFormatter: (value: string) =>
          value === "breadth" ? t("unlabelled") : t("multiplePasses"),
      },
      {
        field: "target_number_of_labellers",
        headerName: t("targetLabellers"),
        type: "number",
        width: 100,
      },
      {
        field: "maximum_number_of_labellers",
        headerName: t("maxLabellers"),
        type: "number",
        width: 100,
      },
      {
        field: "max_duration_seconds",
        headerName: t("maxDuration"),
        type: "number",
        width: 120,
      },
      {
        field: "allowed_coverage_gap_seconds",
        headerName: t("allowedCoverageGap"),
        type: "number",
        width: 120,
      },
      {
        field: "required_angles",
        headerName: t("requiredAngles"),
        width: 200,
        sortable: false,
        filterable: false,
        renderCell: (
          params: GridRenderCellParams<LabelReservationSettingsRead>
        ) => {
          if (!params.value || params.value === "all") {
            return <Chip label="all" size="small" variant="outlined" />;
          }
          const angles = JSON.parse(params.value as string) as string[];
          return (
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              sx={{ overflow: "hidden" }}
            >
              {angles.map((angle) => (
                <Chip key={angle} label={angle} size="small" />
              ))}
            </Stack>
          );
        },
      },
      {
        field: "ignore_coverage",
        headerName: t("ignoreCoverage"),
        width: 120,
        renderCell: (
          params: GridRenderCellParams<LabelReservationSettingsRead>
        ) => (
          <Checkbox checked={params.value as boolean} disabled size="small" />
        ),
      },
      {
        field: "no_reservation_behaviour",
        headerName: t("noReservationBehaviour"),
        width: 160,
        valueFormatter: (value: NoReservationBehaviour) =>
          value === "fallback"
            ? t("behaviourFallback")
            : t("behaviourNoReservation"),
      },
      {
        field: "fallback_priority",
        headerName: t("fallbackPriority"),
        type: "number",
        width: 130,
        sortComparator: (a: number | null, b: number | null) => {
          if (a === null && b === null) return 0;
          if (a === null) return 1;
          if (b === null) return -1;
          return a - b;
        },
        renderCell: ({ row }) => {
          if (row.fallback_priority === null) return null;
          return (
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <span>{row.fallback_priority}</span>
              <Stack>
                <IconButton
                  size="small"
                  disabled={isUpdatingSettings || row.fallback_priority === 0}
                  onClick={() => {
                    handleAdjustPriority(row.id, -1);
                  }}
                  sx={{ p: 0 }}
                >
                  <ArrowDropUpIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  disabled={isUpdatingSettings}
                  onClick={() => {
                    handleAdjustPriority(row.id, 1);
                  }}
                  sx={{ p: 0 }}
                >
                  <ArrowDropDownIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Stack>
          );
        },
      },
      {
        field: "location",
        headerName: t("location"),
        width: 120,
        valueFormatter: (value: string | null) => value ?? "auto",
      },
      {
        field: "reserve_from",
        headerName: t("reserveFrom"),
        width: 180,
        valueFormatter: (value: string | null) =>
          value ? dayjs(value).utc().format("YYYY-MM-DD HH:mm") : "",
      },
      {
        field: "reserve_until",
        headerName: t("reserveUntil"),
        width: 180,
        valueFormatter: (value: string | null) =>
          value ? dayjs(value).utc().format("YYYY-MM-DD HH:mm") : "",
      },
      {
        field: "assigned_users",
        headerName: t("userAssignments"),
        width: 280,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => {
          if (row.default) {
            return (
              <Chip
                label={t("default")}
                color="primary"
                size="small"
                variant="outlined"
              />
            );
          }
          const assignments = assignmentsBySettingsId.get(row.id) ?? [];
          return (
            <Stack
              direction="row"
              flexWrap="wrap"
              rowGap={0.5}
              columnGap={0.5}
              alignItems="center"
              sx={{ py: 0.5 }}
            >
              {assignments.map((a) => (
                <Chip
                  key={a.id}
                  label={a.user_name ?? a.user_id}
                  size="small"
                  onDelete={() => {
                    handleUnassign(a.user_id);
                  }}
                />
              ))}
              <Chip
                icon={<AddIcon />}
                label={t("assignUser")}
                size="small"
                variant="outlined"
                onClick={() => {
                  setAssignDialogSettingsId(row.id);
                }}
              />
            </Stack>
          );
        },
      },
      {
        field: "actions",
        type: "actions",
        width: 140,
        getActions: (params) => {
          const actions = [
            <GridActionsCellItem
              key="edit"
              icon={<EditIcon />}
              label={t("editSettings")}
              onClick={() => {
                setEditing(params.row);
                setFormOpen(true);
              }}
            />,
          ];
          if (!params.row.default) {
            actions.push(
              <GridActionsCellItem
                key="default"
                label={t("setDefault")}
                onClick={() => {
                  handleSetDefault(params.row.id);
                }}
                showInMenu
              />,
              <GridActionsCellItem
                key="delete"
                label={t("deleteSettings")}
                onClick={() => {
                  handleDelete(params.row.id);
                }}
                showInMenu
              />
            );
          }
          return actions;
        },
      },
    ],
    [
      t,
      assignmentsBySettingsId,
      handleAdjustPriority,
      handleSetDefault,
      handleDelete,
      handleUnassign,
      isUpdatingSettings,
    ]
  );

  return (
    <Stack spacing={1} sx={{ flex: 1, minHeight: 300 }}>
      <Stack direction="row" justifyContent="flex-end">
        <Button
          variant="contained"
          onClick={() => {
            setEditing(null);
            setFormOpen(true);
          }}
        >
          {t("createSettings")}
        </Button>
      </Stack>
      <HotkeyCompliantDataGrid
        columns={columns}
        rows={sortedRows}
        loading={isLoading}
        getRowHeight={() => "auto"}
        columnHeaderHeight={44}
        disableRowSelectionOnClick
        getRowId={(row) => row.id}
        getRowClassName={(params) =>
          params.row.default ? "default-settings-row" : ""
        }
        sx={{
          "& .default-settings-row": {
            backgroundColor: "action.hover",
          },
          "& .MuiDataGrid-cell": {
            py: 0.5,
          },
        }}
      />
      {formOpen ? (
        <SettingsProfileForm
          open={formOpen}
          onClose={() => {
            setFormOpen(false);
          }}
          onSubmit={editing ? handleUpdate : handleCreate}
          existing={editing}
          template={editing ? null : defaultRow}
          otherFallbackPriorities={otherFallbackPriorities}
        />
      ) : null}
      {assignDialogSettingsId !== null ? (
        <AssignUserDialog
          open
          onClose={() => {
            setAssignDialogSettingsId(null);
          }}
          users={labellers}
          onAssign={(userId) => {
            handleAssign(assignDialogSettingsId, userId);
          }}
        />
      ) : null}
    </Stack>
  );
};
